import { MutationFunctionOptions } from '@apollo/client';

import {
  SubmitBankTransferRequestInput,
  TeamMembersRequestStatus,
} from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useErrorHandledMutation from 'common/hooks/useErrorHandledMutation';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';

import { ManagedUserBankTransferRequestsDocument } from '../../../../hooks/useManagedUserBankTransferRequests/managedUserBankTransferRequests.gql';
import { RecipientInformationFormSchema } from '../../components/BankTransferRequestNewStepper/steps/RecipientInformation/components/RecipientInformationForm/schema';
import { TransferInformationFormSchema } from '../../components/BankTransferRequestNewStepper/steps/TransferInformation/components/TransferInformationForm/schema';
import {
  SubmitBankTransferRequestDocument,
  SubmitBankTransferRequestMutation,
  SubmitBankTransferRequestMutationVariables,
} from './submitBankTransferRequest.gql';

const mapBankTransferRequestFormValuesToInput = (
  values: RecipientInformationFormSchema & TransferInformationFormSchema,
): Omit<
  SubmitBankTransferRequestInput,
  'bankAccountId' | 'companyProfileId'
> => ({
  amount: Math.round(values.amount * 100),
  bankTransferRecipient: {
    iban: values.iban,
    name: values.name,
    swiftBic: values.swiftBic,
  },
  supportingDocument: {
    bucketName: values.supportingDocument.bucketName,
    fileRef: values.supportingDocument.fileRef,
  },
  userLabel: values.label,
});

export const useSubmitBankTransferRequest = () => {
  const { mainBankAccountId } = useMainBankAccountId();
  const { companyProfileId, companyUserId } = useCompanyContext();

  const [mutation, hooks] = useErrorHandledMutation<
    SubmitBankTransferRequestMutation,
    SubmitBankTransferRequestMutationVariables
  >(SubmitBankTransferRequestDocument);

  const submitBankTransferRequest = (
    input: RecipientInformationFormSchema & TransferInformationFormSchema,
    options?: Omit<
      MutationFunctionOptions<
        SubmitBankTransferRequestMutation,
        SubmitBankTransferRequestMutationVariables
      >,
      'variables'
    >,
  ) =>
    mutation({
      ...options,
      refetchQueries: [
        {
          query: ManagedUserBankTransferRequestsDocument,
          variables: {
            after: null,
            companyProfileId: companyProfileId ?? '',
            requesterCompanyId: companyUserId ?? '',
            status: [TeamMembersRequestStatus.Pending],
          },
        },
      ],
      variables: {
        input: {
          bankAccountId: mainBankAccountId ?? '',
          companyProfileId: companyProfileId ?? '',
          ...mapBankTransferRequestFormValuesToInput(input),
        },
      },
    });

  return [submitBankTransferRequest, hooks] as const;
};
